import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { SplitText } from 'gsap/SplitText'

/**
 * Einheitliche Scroll-Entrances für alle Marketing-Sections — zwei Bausteine:
 *
 *  1. `[data-split]` — Section-Headlines werden per SplitText in Zeilen
 *     zerlegt; jede Zeile steigt aus ihrer eigenen Maske (line mask).
 *     Gesplittet wird erst nach dem Font-Load (sonst stimmen die Umbrüche
 *     nicht), `autoSplit` heilt Resize-Umbrüche von selbst.
 *
 *  2. `.reveal` — Karten/Absätze faden als Batch pro Viewport-Welle ein
 *     (ScrollTrigger.batch: ein Stagger pro Welle statt Delay-Raterei).
 *
 * Beides einmalig (`once`), transform/opacity-only, und bei
 * `prefers-reduced-motion` sofort sichtbar ohne jede Animation.
 */
export function useReveal(container: Ref<HTMLElement | null>) {
  let ctx: gsap.Context | undefined
  let pre: gsap.Context | undefined

  onMounted(async () => {
    await nextTick()
    if (!container.value) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    gsap.registerPlugin(ScrollTrigger, SplitText)

    // Sofort verstecken (kein Aufblitzen), erst nach dem Font-Load messen.
    // `pre` bleibt bis zum Unmount stehen — ein früher Revert würde die
    // danach gesetzten Startzustände von `ctx` gleich mit wegwischen.
    // Nur opacity, nie visibility (autoAlpha): Headlines und Copy bleiben
    // im Accessibility-Tree und für Crawler sichtbar.
    pre = gsap.context(() => {
      gsap.set('.reveal, [data-split]', { opacity: 0 })
    }, container.value)

    // Font-Load abwarten, aber nie länger als 1.5 s — bei blockiertem Font-
    // Host darf der Inhalt nicht unsichtbar hängen bleiben.
    await Promise.race([
      document.fonts?.ready.catch(() => undefined),
      new Promise(resolve => setTimeout(resolve, 1500))
    ])

    if (!container.value) {
      pre.revert()
      return
    }

    ctx = gsap.context((self) => {
      const q = self.selector!

      /* 1 — Headline-Zeilen aus der Maske */
      gsap.utils.toArray<HTMLElement>(q('[data-split]')).forEach((el) => {
        SplitText.create(el, {
          type: 'lines',
          mask: 'lines',
          linesClass: 'split-line',
          autoSplit: true,
          onSplit(instance) {
            gsap.set(el, { opacity: 1 })
            const tl = gsap.timeline({
              scrollTrigger: { trigger: el, start: 'top 88%', once: true }
            })
            tl.from(instance.lines, {
              yPercent: 112,
              duration: 0.9,
              ease: 'power4.out',
              stagger: 0.09
            }, 0)
            // Leichter Blur-In auf der ganzen Headline (nicht pro Zeile — die
            // Masken würden den Blur-Saum hart abschneiden). Der Filter wird
            // nach dem Reveal entfernt, sonst bleibt eine Compositing-Ebene.
            tl.fromTo(el,
              { filter: 'blur(10px)' },
              { filter: 'blur(0px)', duration: 1.0, ease: 'power2.out', clearProps: 'filter' }, 0)
            return tl
          }
        })
      })

      /* 2 — Karten/Absätze im Batch */
      const els = gsap.utils.toArray<HTMLElement>(q('.reveal'))
      if (els.length) {
        gsap.set(els, { opacity: 0, y: 22 })
        ScrollTrigger.batch(els, {
          start: 'top 90%',
          once: true,
          onEnter: batch =>
            gsap.to(batch, {
              opacity: 1,
              y: 0,
              duration: 0.8,
              ease: 'power3.out',
              stagger: 0.08,
              overwrite: true,
              // Inline-Transform wieder freigeben, sonst sind die
              // CSS-Hover-Lifts der Karten nach dem Reveal tot
              onComplete: () => gsap.set(batch, { clearProps: 'transform' })
            })
        })
      }
    }, container.value)
  })

  onBeforeUnmount(() => {
    ctx?.revert()
    pre?.revert()
  })
}
